import { DrizzleAppSchema } from '@powersync/drizzle-driver';
import { relations } from 'drizzle-orm';
import { alerts } from './schemas/alerts.schema';
import { boat_models } from './schemas/boat_models.schema';
import { boat_details } from './schemas/boats.schema';
import { categories } from './schemas/categories.schema';
import { checklist_item_check, checklist_items, checklistables, checklists } from './schemas/checklists.schema';
import { costables, costs } from './schemas/costs.schema';
import { currencies } from './schemas/currencies.schema';
import { field_values, fields } from './schemas/fields.schema';
import { fileables, files, folders } from './schemas/files.schema';
import { followers } from './schemas/followers.schema';
import { inventoriables, inventory } from './schemas/inventory.schema';
import { inventory_location } from './schemas/inventory_location.schema';
import { locations } from './schemas/locations.schema';
import { logs } from './schemas/logs.schema';
import { subscriptions } from './schemas/subscriptions.schema';
import { sync_errors } from './schemas/sync_errors.schema';
import { tasks } from './schemas/tasks.schema';
import { trips } from './schemas/trips.schema';

export const tripRelations = relations(trips, ({ many, one }) => ({
  logs: many(logs),
  boat_details: one(boat_details, {
    fields: [trips.boat_detail_uuid],
    references: [boat_details.id],
  }),
}));

export const tasksRelations = relations(tasks, ({ many, one }) => ({
  boat_details: one(boat_details, {
    fields: [tasks.boat_detail_uuid],
    references: [boat_details.id],
  }),
  category: one(categories, {
    fields: [tasks.category_id],
    references: [categories.laravel_id],
  }),
}));

export const costsRelations = relations(costs, ({ many, one }) => ({
  boat_details: one(boat_details, {
    fields: [costs.boat_detail_uuid],
    references: [boat_details.id],
  }),
  category: one(categories, {
    fields: [costs.category_uuid],
    references: [categories.id],
  }),
}));

export const inventoryRelations = relations(inventory, ({ many, one }) => ({
  boat_details: one(boat_details, {
    fields: [inventory.boat_detail_uuid],
    references: [boat_details.id],
  }),
  category: one(categories, {
    fields: [inventory.category_id],
    references: [categories.laravel_id],
  }),
  inventory_locations: many(inventory_location),
}));

export const inventoryLocationRelations = relations(inventory_location, ({ one }) => ({
  inventory: one(inventory, {
    fields: [inventory_location.inventory_uuid],
    references: [inventory.id],
  }),
  location: one(locations, {
    fields: [inventory_location.location_uuid],
    references: [locations.id],
  }),
}));

export const checklistsRelations = relations(checklists, ({ many, one }) => ({
  items: many(checklist_items),
  checklistables: many(checklistables),
  category: one(categories, {
    fields: [checklists.category_uuid],
    references: [categories.id],
  }),
  image: one(files, {
    fields: [checklists.image_uuid],
    references: [files.id],
  }),
}));

export const checklistItemsRelations = relations(checklist_items, ({ many, one }) => ({
  checklist: one(checklists, {
    fields: [checklist_items.checklist_uuid],
    references: [checklists.id],
  }),
  checks: many(checklist_item_check),
}));

export const checklistItemCheckRelations = relations(checklist_item_check, ({ one }) => ({
  item: one(checklist_items, {
    fields: [checklist_item_check.item_uuid],
    references: [checklist_items.id],
  }),
}));

export const checklistablesRelations = relations(checklistables, ({ one }) => ({
  checklist: one(checklists, {
    fields: [checklistables.checklist_uuid],
    references: [checklists.id],
  }),
}));

export const fieldValuesRelations = relations(field_values, ({ one }) => ({
  field: one(fields, {
    fields: [field_values.field_uuid],
    references: [fields.id],
  }),
}));

export const boatDetailsRelations = relations(boat_details, ({ many }) => ({
  trips: many(trips),
  logs: many(logs),
  tasks: many(tasks),
  inventory: many(inventory),
}));

export type SyncErrorsRecord = typeof sync_errors.$inferSelect;
export type TripsRecord = typeof trips.$inferSelect;
export type BoatDetailsRecord = typeof boat_details.$inferSelect;
export type BoatDetailsRecordInsert = typeof boat_details.$inferInsert;
export type BoatModelRecord = typeof boat_models.$inferSelect;
export type BoatModelRecordInsert = typeof boat_models.$inferInsert;
export type TripsRecordWithBoatDetails = {
  trips: TripsRecord;
  boat_details: BoatDetailsRecord | null;
};
export type TripsRecordInsert = typeof trips.$inferInsert;
export type StoreTripRecord = Omit<typeof trips.$inferInsert, 'id' | 'boat_detail_id'>;
export type LogRecord = typeof logs.$inferInsert;
export type StoredLogRecord = Omit<typeof logs.$inferInsert, 'id'>;
export type StoredTaskRecord = Omit<typeof tasks.$inferInsert, 'id' | 'laravel_id' | 'user_id' | 'created_at'>;
export type FileRecord = typeof files.$inferInsert;
export type FileUpdate = Omit<typeof files.$inferInsert, 'id'>;
export type FolderRecord = typeof folders.$inferInsert;
export type FileableRecord = typeof fileables.$inferInsert;
export type StoreFolderRecord = Omit<typeof folders.$inferInsert, 'id'>;
export type InventoryRecord = typeof inventory.$inferSelect;
export type InventoryRecordInsert = typeof inventory.$inferInsert;
export type StoreInventoryRecord = Omit<typeof inventory.$inferInsert, 'id'>;
export type AlertRecord = typeof alerts.$inferInsert;
export type AlertRecordSelect = typeof alerts.$inferSelect;
export type TaskRecord = Omit<typeof tasks.$inferInsert, 'user_id'>;
export type TaskRecordWithBoat = TaskRecord & { boat_detail_name: string | null };
export type TaskRecordCounter = TaskRecord & { _count: { files: number } };
export type TaskRecordSelect = typeof tasks.$inferSelect;
export type CurrencyRecord = typeof currencies.$inferInsert;
export type LocationRecord = typeof locations.$inferSelect;
export type LocationRecordInsert = typeof locations.$inferInsert;
export type StoreLocationRecord = Omit<typeof locations.$inferInsert, 'id'>;
export type InventoryLocationRecord = typeof inventory_location.$inferSelect;
export type InventoryLocationRecordInsert = typeof inventory_location.$inferInsert;
export type StoreInventoryLocationRecord = Omit<typeof inventory_location.$inferInsert, 'id'>;
export type ChecklistRecord = typeof checklists.$inferSelect;
export type ChecklistRecordInsert = typeof checklists.$inferInsert;
export type StoreChecklistRecord = Omit<typeof checklists.$inferInsert, 'id'>;
export type ChecklistItemRecord = typeof checklist_items.$inferSelect;
export type ChecklistItemRecordInsert = typeof checklist_items.$inferInsert;
export type StoreChecklistItemRecord = Omit<typeof checklist_items.$inferInsert, 'id'>;
export type ChecklistItemCheckRecord = typeof checklist_item_check.$inferSelect;
export type ChecklistItemCheckRecordInsert = typeof checklist_item_check.$inferInsert;
export type StoreChecklistItemCheckRecord = Omit<typeof checklist_item_check.$inferInsert, 'id'>;
export type ChecklistableRecord = typeof checklistables.$inferSelect;
export type ChecklistableRecordInsert = typeof checklistables.$inferInsert;
export type StoreChecklistableRecord = Omit<typeof checklistables.$inferInsert, 'id'>;
export type SubscriptionRecord = typeof subscriptions.$inferSelect;
export type FollowerRecord = typeof followers.$inferSelect;
export type FollowerRecordInsert = typeof followers.$inferInsert;
export type StoreFollowerRecord = Omit<typeof followers.$inferInsert, 'id'>;

export type TripsListItem = Pick<
  TripsRecord,
  | 'id'
  | 'name'
  | 'origin'
  | 'origin_longitude'
  | 'origin_latitude'
  | 'started_at'
  | 'finished_at'
  | 'closed_at'
  | 'auto_logging'
  | 'destination'
  | 'destination_latitude'
  | 'destination_longitude'
  | 'image_uuid'
  | 'laravel_id'
> & {
  boat_id: BoatDetailsRecord['id'];
  boat_name: BoatDetailsRecord['name'];
  boat_flag: BoatDetailsRecord['flag'];
  nr_of_logs: number;
};

/**
 * A boat as the boats list reads her: her own columns, plus the model she is, resolved from
 * `boat_models` in the same query.
 *
 * The model half is nullable twice over. A boat may reference a catalogue model, which lives in
 * Sanity and has no row here, and a boat added before `boat_model_ref` existed references nothing
 * at all. Both read as "the model is not known offline" rather than as an error.
 */
export type BoatsListItem = Pick<
  BoatDetailsRecord,
  | 'id'
  | 'laravel_id'
  | 'name'
  | 'flag'
  | 'year'
  | 'image_uuid'
  | 'location'
  | 'location_toponym'
  | 'enabled'
  | 'boat_model_ref'
  | 'created_at'
> & {
  model_name: BoatModelRecord['model'];
  model_manufacturer: BoatModelRecord['manufacturer_name'];
  /** The model document as json text, for the figures the denormalised columns do not carry. */
  model_data: BoatModelRecord['boat_data'];
};

export const drizzleSchema = {
  trips,
  logs,
  boat_details,
  boat_models,
  sync_errors,
  tripRelations,
  boatDetailsRelations,
  alerts,
  files,
  folders,
  fileables,
  tasks,
  categories,
  inventory,
  inventoryRelations,
  inventoriables,
  costsRelations,
  costs,
  costables,
  currencies,
  locations,
  inventory_location,
  inventoryLocationRelations,
  checklists,
  checklist_items,
  checklist_item_check,
  checklistables,
  checklistsRelations,
  checklistItemsRelations,
  checklistItemCheckRelations,
  checklistablesRelations,
  followers,
  fields,
  field_values,
  fieldValuesRelations,
  subscriptions,
};

export const AppSchema = new DrizzleAppSchema(drizzleSchema);

// For types
export type Database = (typeof AppSchema)['types'];
export type TodoRecord = Database['trips'];
export type CostRecord = Database['costs'];
export type CostRecordInsert = typeof costs.$inferInsert;
export type CostableRecord = typeof costables.$inferInsert;
export type InventoriableRecord = typeof inventoriables.$inferInsert;
